import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for permission management in components
 * Provides utility functions for checking permissions in React components
 */
export const usePermissions = () => {
  const { user, hasPermission, hasRole, isAdmin, isGoogleUser } = useAuth();

  /**
   * Check if user has any of the provided permissions (OR logic)
   * @param {string[]} permissions - Array of permission strings
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Check if user has all of the provided permissions (AND logic)
   * @param {string[]} permissions - Array of permission strings
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Check if user has any of the provided roles
   * @param {string[]} roles - Array of role strings
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    if (!roles || roles.length === 0) return true;
    return roles.some(role => hasRole(role));
  };

  /**
   * Get user's effective permissions for a specific entity
   * @param {string} entity - Entity name (e.g., 'tickets', 'assets')
   * @returns {object} Object containing permission levels for the entity
   */
  const getEntityPermissions = (entity) => {
    const levels = ['read', 'write', 'delete', 'admin', 'approve', 'emergency'];
    const permissions = {};
    
    for (const level of levels) {
      permissions[level] = hasPermission(`${entity}:${level}`);
    }
    
    return permissions;
  };

  /**
   * Check if user can access a specific menu item based on its configuration
   * @param {object} menuItem - Menu item configuration
   * @returns {boolean}
   */
  const canAccessMenuItem = (menuItem) => {
    // If no auth requirement, allow access
    if (!menuItem.auth && !menuItem.admin && !menuItem.permission) {
      return true;
    }

    // Must be authenticated for auth-required items
    if (menuItem.auth && !user) {
      return false;
    }

    // Admin-only items
    if (menuItem.admin && !isAdmin()) {
      return false;
    }

    // Permission-based access
    if (menuItem.permission) {
      return hasPermission(menuItem.permission);
    }

    return true;
  };

  /**
   * Filter menu items based on user permissions
   * @param {array} menuItems - Array of menu item configurations
   * @returns {array} Filtered menu items the user can access
   */
  const getAccessibleMenuItems = (menuItems) => {
    return menuItems.filter(canAccessMenuItem);
  };

  /**
   * Check if user can perform CRUD operations on an entity
   * @param {string} entity - Entity name
   * @returns {object} CRUD permissions for the entity
   */
  const getCrudPermissions = (entity) => {
    return {
      create: hasPermission(`${entity}:write`),
      read: hasPermission(`${entity}:read`),
      update: hasPermission(`${entity}:write`),
      delete: hasPermission(`${entity}:delete`),
      admin: hasPermission(`${entity}:admin`)
    };
  };

  /**
   * Check if user has management permissions for an entity
   * @param {string} entity - Entity name
   * @returns {boolean}
   */
  const canManage = (entity) => {
    return hasPermission(`${entity}:admin`) || hasPermission(`${entity}:write`);
  };

  /**
   * Get the highest permission level the user has for an entity
   * @param {string} entity - Entity name
   * @returns {string} Highest permission level ('read', 'write', 'delete', 'admin', or null)
   */
  const getHighestPermissionLevel = (entity) => {
    const levels = ['admin', 'emergency', 'approve', 'delete', 'write', 'read'];
    
    for (const level of levels) {
      if (hasPermission(`${entity}:${level}`)) {
        return level;
      }
    }
    
    return null;
  };

  /**
   * Check if an integration requires Google authentication
   * @param {string} integration - Integration name
   * @returns {boolean}
   */
  const isGoogleIntegration = (integration) => {
    const googleIntegrations = [
      'googleDrive', 'googleCalendar', 'googleForms', 'googleAdmin', 'googlePeople'
    ];
    return googleIntegrations.includes(integration);
  };

  /**
   * Check if user has permission for integration-specific actions
   * @param {string} integration - Integration name
   * @param {string} action - Action level (read/write/admin)
   * @returns {boolean}
   */
  const hasIntegrationPermission = (integration, action = 'read') => {
    // If it's a Google integration and user is not using Google auth, deny access
    if (isGoogleIntegration(integration) && !isGoogleUser()) {
      return false;
    }
    
    return hasPermission(`${integration}:${action}`);
  };

  /**
   * Check if user can edit payment cards for a specific user
   * Users can edit their own cards, financial managers and admins can edit any user's cards
   * @param {string} targetUserId - The ID of the user whose cards to check
   * @returns {boolean}
   */
  const canEditPaymentCards = (targetUserId) => {
    if (!user) return false;

    // Users can edit their own payment cards
    if (user._id === targetUserId || user.id === targetUserId) {
      return true;
    }

    // Admins can edit any user's payment cards
    if (isAdmin()) {
      return true;
    }

    // Check if user has financial permissions to edit other users' payment cards
    return hasPermission('paymentCards:all') || hasPermission('paymentCards:admin');
  };

  /**
   * Get all integrations the user has access to
   * @param {string} minPermission - Minimum permission level required (default: 'read')
   * @returns {array} Array of integration names the user can access
   */
  const getAccessibleIntegrations = (minPermission = 'read') => {
    // Define all available integrations
    const googleIntegrations = [
      'googleDrive', 'googleCalendar', 'googleForms', 'googleAdmin', 'googlePeople'
    ];
    
    const otherIntegrations = [
      // Security Systems
      'lenelS2NetBox', 'unifiAccess', 'unifiProtect', 
      // Network Systems
      'unifiNetwork', 'radius', 'wifi',
      // Smart Building Controls
      'wiim', 'dreo', 'lgThinq', 'govee', 'skyportcloud', 'rainBird', 'colorlit',
      // AV Production Systems
      'qsys', 'canon',
      // Business Tools
      'canva', 'planningCenter', 'constantContact', 'synology',
      // Device Management
      'appleBusinessManager', 'mosyleBusiness',
      // Building Infrastructure (if needed as integrations)
      'bms', 'electrical', 'hvac'
    ];

    // For local users, exclude Google integrations
    const availableIntegrations = isGoogleUser() ? 
      [...googleIntegrations, ...otherIntegrations] : 
      otherIntegrations;

    return availableIntegrations.filter(integration => 
      hasPermission(`${integration}:${minPermission}`)
    );
  };

  return {
    // Basic permission checks
    hasPermission,
    hasRole,
    isAdmin,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyRole,
    
    // Entity-specific utilities
    getEntityPermissions,
    getCrudPermissions,
    canManage,
    getHighestPermissionLevel,
    
    // Menu and navigation utilities
    canAccessMenuItem,
    getAccessibleMenuItems,
    
    // Integration utilities
    isGoogleIntegration,
    hasIntegrationPermission,
    getAccessibleIntegrations,
    
    // Payment card utilities
    canEditPaymentCards,
    
    // User info
    user,
    isGoogleUser
  };
};